import React from 'react';
import { LoginBackground, LoginTextOverlay, TabletLoginImage } from '../../assets/images';

/**
 * AuthImage Component
 *
 * A responsive background component specifically designed for authentication-related screens
 * (e.g., Login, Set New Password). It displays a unified background image, an optional custom text overlay,
 * and a tablet illustration (visible on larger screens).
 *
 * Key Features:
 * - Unified background image using 'object-cover' to adapt to container size.
 * - Adaptive text overlay with responsive positioning and scaling.
 * - Accepts a custom text overlay image via the `TextOverlaySrc` prop, enabling reuse in different contexts
 *   (e.g., Login page, AcceptInvitation page).
 * - Tablet illustration is conditionally rendered on larger screens for enhanced desktop visuals.
 *
 * @param {Object} props - Component properties.
 * @param {string} [props.TextOverlaySrc=LoginTextOverlay] - The source path for the text overlay image.
 *   Defaults to the Login page text overlay if not provided.
 *
 * @returns {JSX.Element} A responsive background component for authentication.
 */
const containerClasses = 'relative lg:h-screen md:h-64 h-40 lg:order-last';
const backgroundImageClasses = 'w-full h-full object-cover';
const overlayTextBaseClasses =
  'absolute left-1/2 -translate-x-1/2 z-20 w-3/4 top-1/2 -translate-y-1/2 scale-75';
const overlayTextLgClasses = 'lg:w-2/3 lg:top-[20%] lg:scale-90';
const overlayTextMdClasses = 'md:w-3/4 md:top-1/2 md:-translate-y-1/2 md:scale-75';
const tabletIllustrationClasses =
  'hidden lg:block absolute w-4/5 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/3 z-10 scale-95';

const AuthImage = ({ TextOverlaySrc = LoginTextOverlay }) => (
  <div className={containerClasses}>
    {/* Background image */}
    <img src={LoginBackground} alt="Background" className={backgroundImageClasses} />

    {/* Text Overlay */}
    <img
      src={TextOverlaySrc}
      alt="Text overlay"
      className={`${overlayTextBaseClasses} ${overlayTextLgClasses} ${overlayTextMdClasses}`}
    />

    {/* Tablet illustration */}
    <img src={TabletLoginImage} alt="Tablet illustration" className={tabletIllustrationClasses} />
  </div>
);

export default AuthImage;
